import { useMemo } from "react";
import type { FlowGraph, FlowNode, PageCategory } from "../lib/api";

/**
 * Shared SVG renderer for crawl flow graphs.
 *
 * Two consumers:
 *
 *   1. /crawls/:id (CrawlMapPage) — colors nodes by their semantic category
 *      (auth / commerce / content / shell / errors). Pass `colorByUrl =
 *      undefined` to get the legacy behavior.
 *
 *   2. /coverage (CoveragePage) — colors nodes by Plan Journey-5 coverage
 *      state (passed = green, failed = amber, untested = slate). Pass a
 *      `colorByUrl(url)` that returns the status string per URL.
 *
 * The layout (BFS layering, bezier edges) is identical across both — the
 * only branch is the per-node fill. Keeping one renderer means a layout
 * fix lands in both views at once.
 */

export type CoverageNodeStatus = "passed" | "failed" | "untested";

export interface FlowGraphSvgProps {
  graph: FlowGraph;
  /** Currently-selected URL (highlighted with an indigo ring). null = none. */
  selectedUrl?: string | null;
  /** Click handler. URL of the clicked node. Optional — when omitted the
   *  rect renders with `cursor: default` and ignores clicks. */
  onSelect?: (url: string) => void;
  /**
   * When provided, overrides the per-category fill with a status-driven
   * color (Coverage Map mode). Returning `null`/`undefined` falls back to
   * the legacy category color so a partially-untested project still
   * shows category info on the unmatched nodes.
   */
  colorByUrl?: (url: string) => CoverageNodeStatus | null | undefined;
}

const NODE_W = 132;
const NODE_H = 44;
const LAYER_GAP_X = 56;
const NODE_GAP_Y = 20;
const PADDING = 24;

interface GraphLayout {
  width: number;
  height: number;
  positioned: Map<string, { x: number; y: number }>;
}

/**
 * Assign each node a (layer, slot) via BFS from in-degree-zero roots.
 * Layer = horizontal column; slot = vertical position within the column.
 * Cycles are handled by the BFS visited-set — nodes already placed don't
 * get re-laid-out.
 */
function layoutGraph(graph: FlowGraph): GraphLayout {
  const byUrl = new Map(graph.nodes.map((n) => [n.url, n]));
  const indeg = new Map<string, number>();
  for (const n of graph.nodes) indeg.set(n.url, 0);
  for (const e of graph.edges) {
    if (byUrl.has(e.toUrl)) indeg.set(e.toUrl, (indeg.get(e.toUrl) ?? 0) + 1);
  }

  const adj = new Map<string, string[]>();
  for (const n of graph.nodes) adj.set(n.url, []);
  for (const e of graph.edges) {
    if (byUrl.has(e.fromUrl) && byUrl.has(e.toUrl)) {
      adj.get(e.fromUrl)?.push(e.toUrl);
    }
  }

  const layer = new Map<string, number>();
  const queue: string[] = [];
  for (const n of graph.nodes) {
    if ((indeg.get(n.url) ?? 0) === 0) {
      layer.set(n.url, 0);
      queue.push(n.url);
    }
  }
  // Handle pure-cyclic graphs with no in-degree-zero nodes by seeding
  // layer 0 with the first node — otherwise BFS never starts.
  if (queue.length === 0 && graph.nodes.length > 0) {
    const first = graph.nodes[0];
    if (first) {
      layer.set(first.url, 0);
      queue.push(first.url);
    }
  }

  while (queue.length > 0) {
    const url = queue.shift();
    if (url === undefined) break;
    const d = layer.get(url) ?? 0;
    for (const next of adj.get(url) ?? []) {
      if (!layer.has(next)) {
        layer.set(next, d + 1);
        queue.push(next);
      }
    }
  }

  // Anything unreached (disconnected components) goes to layer 0.
  for (const n of graph.nodes) if (!layer.has(n.url)) layer.set(n.url, 0);

  // Group by layer.
  const byLayer = new Map<number, FlowNode[]>();
  for (const n of graph.nodes) {
    const d = layer.get(n.url) ?? 0;
    const bucket = byLayer.get(d) ?? [];
    bucket.push(n);
    byLayer.set(d, bucket);
  }

  // Compute positions.
  const layers = Array.from(byLayer.keys()).sort((a, b) => a - b);
  const maxSlots = Math.max(0, ...Array.from(byLayer.values()).map((v) => v.length));
  const width =
    PADDING * 2 + layers.length * NODE_W + Math.max(0, layers.length - 1) * LAYER_GAP_X;
  const height =
    PADDING * 2 + maxSlots * NODE_H + Math.max(0, maxSlots - 1) * NODE_GAP_Y;

  const positioned = new Map<string, { x: number; y: number }>();
  layers.forEach((d, i) => {
    const bucket = byLayer.get(d) ?? [];
    const colX = PADDING + i * (NODE_W + LAYER_GAP_X);
    const colHeight =
      bucket.length * NODE_H + Math.max(0, bucket.length - 1) * NODE_GAP_Y;
    const startY = PADDING + (height - PADDING * 2 - colHeight) / 2;
    bucket.forEach((n, j) => {
      const y = startY + j * (NODE_H + NODE_GAP_Y);
      positioned.set(n.url, { x: colX, y });
    });
  });

  return {
    width: Math.max(width, 320),
    height: Math.max(height, 120),
    positioned,
  };
}

function edgePath(
  from: { x: number; y: number },
  to: { x: number; y: number },
): string {
  const x1 = from.x + NODE_W;
  const y1 = from.y + NODE_H / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_H / 2;
  const cx = (x1 + x2) / 2;
  // Curve intensity scales with vertical distance — flat edges stay
  // straight, big jumps bow out nicely.
  return `M ${x1},${y1} C ${cx},${y1} ${cx},${y2} ${x2},${y2}`;
}

export function FlowGraphSvg({
  graph,
  selectedUrl = null,
  onSelect,
  colorByUrl,
}: FlowGraphSvgProps): JSX.Element {
  const layout = useMemo(() => layoutGraph(graph), [graph]);
  const { width, height, positioned } = layout;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className="block"
      style={{ maxWidth: "100%" }}
    >
      <defs>
        <marker
          id="flow-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill="rgb(100 116 139)" />
        </marker>
      </defs>

      {/* Edges first so nodes render on top. */}
      {graph.edges.map((e, i) => {
        const from = positioned.get(e.fromUrl);
        const to = positioned.get(e.toUrl);
        if (!from || !to) return null;
        const d = edgePath(from, to);
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="rgb(100 116 139)"
            strokeOpacity={0.5}
            strokeWidth={1.5}
            markerEnd="url(#flow-arrow)"
          />
        );
      })}

      {graph.nodes.map((n) => {
        const pos = positioned.get(n.url);
        if (!pos) return null;
        const active = n.url === selectedUrl;
        const status = colorByUrl?.(n.url) ?? null;
        return (
          <FlowNodeShape
            key={n.url}
            node={n}
            x={pos.x}
            y={pos.y}
            active={active}
            status={status}
            {...(onSelect ? { onClick: () => onSelect(n.url) } : {})}
          />
        );
      })}
    </svg>
  );
}

function FlowNodeShape({
  node,
  x,
  y,
  active,
  status,
  onClick,
}: {
  node: FlowNode;
  x: number;
  y: number;
  active: boolean;
  status: CoverageNodeStatus | null;
  onClick?: () => void;
}): JSX.Element {
  // Status mode (Coverage page) overrides the category palette. When
  // status is null, fall back to the existing per-category colors.
  const fill = status
    ? svgFillForStatus(status)
    : svgFillForCategory(node.category);
  const stroke = active
    ? "rgb(129 140 248)"
    : status
      ? svgStrokeForStatus(status)
      : svgStrokeForCategory(node.category);
  const pct = Math.round(node.confidence * 100);
  const subline = status
    ? `${node.category} · ${statusLabel(status)}`
    : `${node.category} · ${pct}%`;
  return (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ cursor: onClick ? "pointer" : "default" }}
      {...(onClick ? { onClick } : {})}
    >
      <title>{`${node.label} (${pct}% · ${node.category})${status ? ` · ${statusLabel(status)}` : ""}\n${node.title || node.url}`}</title>
      <rect
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill={fill}
        stroke={stroke}
        strokeWidth={active ? 2 : 1}
      />
      <text
        x={NODE_W / 2}
        y={NODE_H / 2 - 3}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="rgb(226 232 240)"
      >
        {truncateLabel(node.label, 16)}
      </text>
      <text
        x={NODE_W / 2}
        y={NODE_H / 2 + 12}
        textAnchor="middle"
        fontSize={10}
        fill="rgb(148 163 184)"
        fontFamily="monospace"
      >
        {subline}
      </text>
    </g>
  );
}

function statusLabel(s: CoverageNodeStatus): string {
  return s === "passed" ? "passed" : s === "failed" ? "failed" : "untested";
}

function truncateLabel(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

// ─── Category palette (CrawlMap mode) ────────────────────────────────

function svgFillForCategory(cat: PageCategory): string {
  switch (cat) {
    case "auth":
      return "rgb(67 56 202 / 0.25)";
    case "commerce":
      return "rgb(5 150 105 / 0.25)";
    case "content":
      return "rgb(14 165 233 / 0.22)";
    case "account":
      return "rgb(217 119 6 / 0.25)";
    case "shell":
      return "rgb(71 85 105 / 0.25)";
    case "errors":
      return "rgb(225 29 72 / 0.25)";
  }
}

function svgStrokeForCategory(cat: PageCategory): string {
  switch (cat) {
    case "auth":
      return "rgb(129 140 248 / 0.7)";
    case "commerce":
      return "rgb(52 211 153 / 0.7)";
    case "content":
      return "rgb(56 189 248 / 0.7)";
    case "account":
      return "rgb(251 191 36 / 0.7)";
    case "shell":
      return "rgb(148 163 184 / 0.7)";
    case "errors":
      return "rgb(251 113 133 / 0.7)";
  }
}

// ─── Status palette (Coverage mode) ──────────────────────────────────

function svgFillForStatus(s: CoverageNodeStatus): string {
  switch (s) {
    case "passed":
      return "rgb(5 150 105 / 0.30)";
    case "failed":
      return "rgb(225 29 72 / 0.30)";
    case "untested":
      return "rgb(71 85 105 / 0.25)";
  }
}

function svgStrokeForStatus(s: CoverageNodeStatus): string {
  switch (s) {
    case "passed":
      return "rgb(52 211 153 / 0.85)";
    case "failed":
      return "rgb(251 113 133 / 0.85)";
    case "untested":
      return "rgb(148 163 184 / 0.6)";
  }
}
